/**
 * WebCrypto(AES-GCM) 기반 문자열/JSON 암복호화.
 *
 * ★ 키가 프론트 번들에 들어 있으므로 "기밀 유지"가 아니라
 *   "브라우저 스토리지 평문 노출 방지(난독화)" 목적으로만 쓴다.
 */

export type EncryptedPayload = {
  iv: number[];
  cipher: number[];
};

export interface CryptoOptions {
  secret: string;
}

/**
 * WebCrypto 의 `crypto.subtle` 은 **보안 컨텍스트에서만** 노출된다.
 * https, localhost, 127.0.0.1, file:// 은 되고 그 외 평문 http 는 안 된다.
 *
 * 그래서 `http://dev.urnplay.com` 처럼 커스텀 도메인 + http 로 접속하면
 * `crypto.subtle` 이 undefined 라 세션 저장(로그인 유지)과 API 본문 암호화가
 * 전부 실패한다. 원인을 모르면 "로그인은 되는데 화면이 계속 로그인으로 돌아가는"
 * 증상으로만 보이므로 여기서 분명하게 알린다.
 */
export function isCryptoAvailable(): boolean {
  return typeof crypto !== 'undefined' && !!crypto.subtle;
}

export function assertSecureContext(): void {
  if (isCryptoAvailable()) return;

  throw new Error(
    '브라우저 암호화 기능(crypto.subtle)을 쓸 수 없습니다. ' +
      `현재 주소(${location.origin})가 보안 컨텍스트가 아닙니다. ` +
      'https 로 접속하거나 http://localhost 를 사용하세요.'
  );
}

export class Cipher {
  private readonly secret: string;
  private keyPromise: Promise<CryptoKey> | null = null;

  constructor(opts: CryptoOptions) {
    this.secret = opts.secret;
  }

  /** secret 문자열 -> SHA-256 으로 32바이트 파생 -> AES-GCM 키 */
  private getKey(): Promise<CryptoKey> {
    this.keyPromise ??= (async () => {
      assertSecureContext();

      const data = new TextEncoder().encode(this.secret);
      const hash = await crypto.subtle.digest('SHA-256', data);
      return crypto.subtle.importKey('raw', hash, { name: 'AES-GCM' }, false, [
        'encrypt',
        'decrypt',
      ]);
    })();

    return this.keyPromise;
  }

  async encrypt(plainText: string): Promise<EncryptedPayload> {
    const key = await this.getKey();
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const encoded = new TextEncoder().encode(plainText);

    const cipherBuf = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, encoded);

    return {
      iv: Array.from(iv),
      cipher: Array.from(new Uint8Array(cipherBuf)),
    };
  }

  async decrypt(payload: EncryptedPayload): Promise<string> {
    const key = await this.getKey();
    const iv = new Uint8Array(payload.iv);
    const cipher = new Uint8Array(payload.cipher);

    const plainBuf = await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, key, cipher);
    return new TextDecoder().decode(plainBuf);
  }

  async encryptJson<T>(value: T): Promise<EncryptedPayload> {
    return this.encrypt(JSON.stringify(value));
  }

  async decryptJson<T>(payload: EncryptedPayload): Promise<T> {
    return JSON.parse(await this.decrypt(payload)) as T;
  }
}
